import { components } from "@/api-schema";
import {
  PathsV1CompanySearchPostParametersQuerySelect,
  PathsV1UserSearchPostParametersQuerySelect,
  UserStatus,
  UserType,
} from "@/api-schema-enums";
import { useCompanySearchQuery } from "@/common/api/company/use-company-search.query";
import { useUserInviteGuestMutation } from "@/common/api/user/use-user-invite-guest.mutation";
import { useUserSearchQuery } from "@/common/api/user/use-user-search.query";
import { toastMutationStatus } from "@/common/helpers/toast-mutation-status";
import { Card, CardContent } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { FormCardActions } from "@/components/ui/form-card";
import { Input } from "@/components/ui/input";
import { Loader, LoaderSize, LoaderVariant } from "@/components/ui/loader";
import { MultiSelect } from "@/components/ui/multi-select";
import { Skeleton } from "@/components/ui/skeleton";
import i18next from "@/i18n/config";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMemo } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useLocation } from "wouter";
import { z } from "zod";

type User = components["schemas"]["User"];
type Company = components["schemas"]["Company"];

const InviteGuestFormSchema = z.object({
  firstName: z.string().min(1, i18next.t("masterdata:FirstNameIsRequired")),
  lastName: z.string().min(1, i18next.t("masterdata:LastNameIsRequired")),
  email: z.email().min(1, i18next.t("masterdata:EmailIsRequired")),
  companies: z.array(z.string()).optional(),
});

export type InviteGuestFormSchemaType = z.infer<typeof InviteGuestFormSchema>;

export function InviteGuestFormFields() {
  const { t } = useTranslation(["common", "masterdata"]);
  const [_, navigate] = useLocation();
  const { mutateAsync, isPending } = useUserInviteGuestMutation();
  const form = useForm<InviteGuestFormSchemaType>({
    resolver: zodResolver(InviteGuestFormSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      companies: [],
    },
  });

  const watchedEmail = useWatch({
    control: form.control,
    name: "email",
  });

  const { data: existingUsers, isPending: isExistingUsersLoading } = useUserSearchQuery({
    options: {
      body: {},
      parameters: {
        pageSize: -1,
        select: [PathsV1UserSearchPostParametersQuerySelect.Uuid, PathsV1UserSearchPostParametersQuerySelect.Email],
      },
    },
  });

  const { data: companies, isPending: isCompaniesLoading } = useCompanySearchQuery({
    options: {
      body: {},
      parameters: {
        pageSize: -1,
        select: [
          PathsV1CompanySearchPostParametersQuerySelect.Uuid,
          PathsV1CompanySearchPostParametersQuerySelect.Name,
        ],
      },
    },
  });

  const mappedCompanies = useMemo(
    () => companies?.data?.map((company: Company) => ({ value: company.uuid, label: company.name })) || [],
    [companies],
  );

  const onSubmit = async (values: InviteGuestFormSchemaType) => {
    try {
      await toastMutationStatus({
        mutationFn: () =>
          mutateAsync({
            firstName: values.firstName,
            lastName: values.lastName,
            email: values.email,
            type: UserType.Guest,
            status: UserStatus.Enabled,
            companies: values.companies,
          }),
        successMessage: t("masterdata:GuestInvited"),
        errorMessage: t("masterdata:ErrorInvitingGuest"),
      });

      navigate("~/masterdata/user");
    } catch (error) {
      console.error("Error inviting guest user:", error);
    }
  };

  const duplicateValidation = useMemo(() => {
    if (!existingUsers?.data || !Array.isArray(existingUsers.data)) {
      return { hasErrors: false, errors: [] };
    }

    const errors: string[] = [];

    if (watchedEmail?.trim()) {
      const emailLower = watchedEmail.toLowerCase().trim();
      if (emailLower) {
        const duplicateEmail = existingUsers.data.find((user: User) => user.email?.toLowerCase() === emailLower);
        if (duplicateEmail) {
          errors.push(t("masterdata:UserEmailAlreadyExists", { email: emailLower }));
        }
      }
    }

    return { hasErrors: errors.length > 0, errors };
  }, [existingUsers, watchedEmail, t]);

  const handleSubmitWithValidation = (values: InviteGuestFormSchemaType) => {
    if (duplicateValidation.hasErrors) {
      duplicateValidation.errors.forEach((error) => {
        toast.error(t("common:ValidationError"), {
          description: error,
          duration: 5000,
        });
      });
      return;
    }
    onSubmit(values);
  };

  const isLoading = isExistingUsersLoading || isCompaniesLoading;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="space-y-6">
          <div className="space-y-2">
            <Skeleton className="h-4 w-12" />
            <Skeleton className="h-10 w-full rounded-md" />
          </div>

          <div className="space-y-2">
            <Skeleton className="h-4 w-12" />
            <Skeleton className="h-10 w-full rounded-md" />
          </div>

          <div className="space-y-2">
            <Skeleton className="h-4 w-12" />
            <Skeleton className="h-10 w-full rounded-md" />
          </div>

          <div className="space-y-2">
            <Skeleton className="h-4 w-12" />
            <Skeleton className="h-10 w-full rounded-md" />
          </div>

          <div className="flex justify-end space-x-4">
            <Skeleton className="h-10 w-24 rounded-md" />
            <Skeleton className="h-10 w-24 rounded-md" />
          </div>
        </div>
      </div>
    );
  }

  const isSubmitDisabled = isPending || duplicateValidation.hasErrors || !form.formState.isValid;

  return (
    <Form {...form}>
      <form className="space-y-6" onSubmit={form.handleSubmit(handleSubmitWithValidation)}>
        <Card>
          <CardContent className="space-y-6">
            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("common:FirstName")} *</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input placeholder={t("masterdata:FirstNameExample")} {...field} />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("common:LastName")} *</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input placeholder={t("masterdata:LastNameExample")} {...field} />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("common:Email")} *</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input
                        placeholder={t("masterdata:EmailPlaceholder")}
                        {...field}
                        error={duplicateValidation.errors.some((e) => e.includes(t("masterdata:Email")))}
                        disabled={isExistingUsersLoading}
                      />
                      {isExistingUsersLoading && (
                        <div className="absolute top-1/2 right-3 -translate-y-1/2 transform">
                          <Loader size={LoaderSize.Small} variant={LoaderVariant.Wheel} />
                        </div>
                      )}
                    </div>
                  </FormControl>
                  <FormMessage />
                  {isExistingUsersLoading ? (
                    <p className="text-muted-foreground mt-1 text-sm">{t("masterdata:CheckingDuplicateEmails")}</p>
                  ) : (
                    duplicateValidation.errors.some((e) => e.includes(t("masterdata:Email"))) && (
                      <p className="text-error mt-1 text-sm">{t("masterdata:UserWithThisEmailAlreadyExists")}</p>
                    )
                  )}
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="companies"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>{t("common:Companies")}</FormLabel>
                  <MultiSelect
                    options={mappedCompanies}
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                    placeholder={isCompaniesLoading ? t("masterdata:CompaniesAreLoading") : t("common:SelectCompanies")}
                    variant="secondary"
                    maxCount={10}
                    disabled={isCompaniesLoading}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
        </Card>

        <FormCardActions
          cancelLabel={t("common:Cancel")}
          onCancel={() => window.history.back()}
          isPending={isPending}
          submitDisabled={isSubmitDisabled}
          submitLabel={t("common:Save")}
        />
      </form>
    </Form>
  );
}
